import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AuthorizedRoutes } from 'app/routing/authorizedRoutes';
import { useLocation, useNavigate } from 'react-router-dom';
import 'antd/dist/reset.css';
import { AppLayout } from 'widgets';
import { notification } from 'antd';
import flowersImg from 'assets/images/loginNewBg.svg';
import { ACCESS_TOKEN_KEY } from 'shared/config';
import { ROUTES } from 'app/routing/const/routes';
import {
  selectUserAuthorizedModules,
  selectUserCompaniesListData,
  selectUserCurrentCompany,
  selectUserInfoFullName,
  selectUserInfoStatus,
  selectUserTermsOfUseAcceptance,
  selectIsMainUser,
  selectIsUserNotification,
  selectIsUserNextNotification,
  selectAccountingAccessLink,
} from 'modules/loggedUserInfo/selectors';
import {
  changeCurrentCompany,
  fetchIsanetRedirectUrl,
  loggedUserInfoRequest,
  setCurrentCompanyApiKeyRequest,
  userCompaniesDashboardDataRequest,
  goToNextNotification,
  getAccountingAccessLink,
} from 'modules/loggedUserInfo/actions';
// import { getCarbonEmission } from 'modules/Carbon/actions';
import { Drawer, message } from 'antd';
import { API_KEY_MODAL_TRANSLATIONS, GENERAL_ERRORS } from 'const/translations';
import {
  HELP_NAV_LINK,
  NAV_LINKS,
  SIDEBAR_CUSTOM_ACTIONS,
} from 'const/Sidebar';
import { SidebarNavLinks } from 'components/SidebarNavLinks';
import { TermsOfUseRoutes } from 'routing/TermsOfUseRoutes';
import { RegisteredUserTermsRoutes } from 'routing/RegisteredUserTermsRoutes';
import { STATUS_LOADING, STATUS_NOT_REQUESTED, APP_MODULES } from 'const';
import { Spinner } from 'components/Spinner';
import MainStyles from 'assets/styles/main.module.css';
import { getPackagesCostsRequest, initializeCart } from 'modules/cart/actions';
import clsx from 'clsx';
import { getScreenWidthBreakpointMedia, HistoryNavigator } from 'shared/lib';
import { useSimpleMedia } from 'hooks/useMedia';
import { selectIsNPSVisible } from 'modules/NPSModal/selectors';
import { waibiAccessRequest } from 'modules/dashboardIndicators/action';
import { selectIsWaibiLoading } from 'modules/dashboardIndicators/selectors';
import { getFromStorage, setToStorage } from 'helpers/storage';
import { SalaryHeader } from 'components/SalaryComponents/SalaryHeader';
import { selectPackagesCostsStatus } from 'modules/cart/selectors';
import { openModal } from 'modules/ModalContainer/actions';
import { selectModalState } from 'modules/ModalContainer/selectors';
import { MODAL_TYPES } from 'const/ModalContainer';
import { useMatomoTagManager } from 'hooks/useMatomoTagManager';

import Styles from './App.module.css';

const isLoggedIn = Boolean(
  getFromStorage({ key: ACCESS_TOKEN_KEY, storage: sessionStorage })
);

const SCREEN_WIDTH_BREAKPOINT_MEDIA = getScreenWidthBreakpointMedia(1300);

/**
 * All independent modals accessible anywhere are placed in header component.
 */
export const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = location.pathname;

  HistoryNavigator.init(navigate);

  const isLaptopVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);

  const registeredCompanies = useSelector(selectUserCompaniesListData);
  const userInfoStatus = useSelector(selectUserInfoStatus);
  const userInfoFullName = useSelector(selectUserInfoFullName);
  const currentCompany = useSelector(selectUserCurrentCompany);
  const userAuthorisedModules = useSelector(selectUserAuthorizedModules);
  const packagesCostsStatus = useSelector(selectPackagesCostsStatus);
  const modalProps = useSelector(selectModalState);
  const isMainUser = useSelector(selectIsMainUser);
  const accountingAccessLinks = useSelector(selectAccountingAccessLink);
  const nextNotification = useSelector(selectIsUserNextNotification);

  useMatomoTagManager();

  useEffect(() => {
    if (!isLoggedIn) {
      window.location.href = ROUTES.LOGIN_WITH_SSO;
    }
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(loggedUserInfoRequest());
      dispatch(userCompaniesDashboardDataRequest());
      dispatch(initializeCart());
    }
  }, [dispatch]);

  // useEffect(() => {
  //   if (isLoggedIn) {
  //     dispatch(getCarbonEmission());
  //   }
  // }, [dispatch, isLoggedIn, currentCompany]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('jwt');

    if (token) {
      setToStorage({
        key: ACCESS_TOKEN_KEY,
        value: token,
        storage: sessionStorage,
      });

      window.location.href = '/';
    }
  }, [location, navigate]);

  useEffect(() => {
    if (
      isLoggedIn &&
      userAuthorisedModules?.includes(APP_MODULES.ACCT_ACCESS)
    ) {
      dispatch(getAccountingAccessLink());
    }
  }, [dispatch, isLoggedIn, userAuthorisedModules]);

  useEffect(() => {
    if (
      isLoggedIn &&
      isMainUser &&
      packagesCostsStatus === STATUS_NOT_REQUESTED
    ) {
      dispatch(getPackagesCostsRequest());
    }
  }, [dispatch, isMainUser, packagesCostsStatus, userAuthorisedModules]);

  const [isSideBarOpen, setIsSideBarOpen] = useState(false);

  const isSideBarOpenToggle = useCallback(() => {
    setIsSideBarOpen((prev) => !prev);
  }, [setIsSideBarOpen]);

  const [isSideBarFullWidth, setIsSideBarFullWidth] = useState(false);
  const isSideBarFullWidthToggle = useCallback(() => {
    setIsSideBarFullWidth((prev) => !prev);
  }, [setIsSideBarFullWidth]);

  useEffect(() => {
    setIsSideBarOpen(false);
    setIsSideBarFullWidth(false);
  }, [setIsSideBarFullWidth, setIsSideBarOpen, isLaptopVersion]);

  const handleCompanyChange = useCallback(
    (value) => dispatch(changeCurrentCompany({ companyId: value })),
    [dispatch]
  );

  const handleSetApiKey = useCallback(
    (value) => {
      if (!value) {
        return message.error(API_KEY_MODAL_TRANSLATIONS.ERROR_EMPTY);
      }

      return dispatch(
        setCurrentCompanyApiKeyRequest({
          apiKey: value,
          onError: (errorMessage) => message.error(errorMessage),
        })
      );
    },
    [dispatch]
  );

  const handleIsanetRedirect = useCallback(() => {
    if (!currentCompany.ssoEnabled) {
      message.error(GENERAL_ERRORS.SSO_DISABLED);
      return;
    }
    dispatch(
      fetchIsanetRedirectUrl({
        onError: (errorMessage) => message.error(errorMessage),
      })
    );
  }, [currentCompany.ssoEnabled, dispatch]);

  const handleCloseAndRedirectToIsanet = useCallback(() => {
    isSideBarOpenToggle();
    handleIsanetRedirect();
  }, [isSideBarOpenToggle, handleIsanetRedirect]);

  const handleWaibiRedirect = useCallback(() => {
    dispatch(
      waibiAccessRequest({
        onError: (errorMessage) => message.error(errorMessage),
      })
    );
  }, [dispatch]);

  const handleCloseAndRedirectToWaibi = useCallback(() => {
    isSideBarOpenToggle();
    handleWaibiRedirect();
  }, [isSideBarOpenToggle, handleWaibiRedirect]);

  const handleGoToAccounting = (accessLink) => {
    if (accessLink) {
      window.open(accessLink);
    } else {
      notification.error({ message: 'Lien non disponible' });
    }
  };

  const isWaibiLoading = useSelector(selectIsWaibiLoading);

  const handleCustomActions = useCallback(
    (event) => {
      const { actionType } = event.currentTarget.dataset;

      isSideBarOpenToggle();

      if (!actionType) {
        return;
      }

      if (actionType === SIDEBAR_CUSTOM_ACTIONS.helpRequest) {
        dispatch(openModal({ type: MODAL_TYPES.requestHelp, modalProps: {} }));
      }
      if (
        actionType === SIDEBAR_CUSTOM_ACTIONS.waibiRedirect &&
        !isWaibiLoading
      ) {
        handleCloseAndRedirectToWaibi();
      }

      if (actionType === SIDEBAR_CUSTOM_ACTIONS.accounting) {
        handleGoToAccounting(accountingAccessLinks.accessLink);
      }

      if (actionType === SIDEBAR_CUSTOM_ACTIONS.approvedPlatform) {
        handleGoToAccounting(accountingAccessLinks.myPlatformAccessLink);
      }
    },
    [
      dispatch,
      isSideBarOpenToggle,
      isWaibiLoading,
      handleCloseAndRedirectToWaibi,
      accountingAccessLinks,
    ]
  );

  const areTermsOfUseAccepted = useSelector(selectUserTermsOfUseAcceptance);
  const isUserAdditional = useMemo(() => !isMainUser, [isMainUser]);

  const isNPSVisible = useSelector(selectIsNPSVisible);
  const isNotificationsVisible = useSelector(selectIsUserNotification);

  useEffect(() => {
    if (
      areTermsOfUseAccepted &&
      isLoggedIn &&
      isNPSVisible &&
      !isNotificationsVisible
    ) {
      dispatch(openModal({ type: MODAL_TYPES.netPromoterScore }));
    }
  }, [areTermsOfUseAccepted, dispatch, isNPSVisible, isNotificationsVisible]);

  const goToNextNotificationOnClick = useCallback(
    (handleClose) => () => {
      if (!nextNotification) {
        handleClose();
      }
      dispatch(goToNextNotification());
    },
    [dispatch, nextNotification]
  );

  useEffect(() => {
    if (areTermsOfUseAccepted && isLoggedIn && isNotificationsVisible) {
      dispatch(
        openModal({
          type: MODAL_TYPES.informationModal,
          modalProps: {
            closable: true,
            onClose: goToNextNotificationOnClick,
          },
        })
      );
    }
  }, [
    areTermsOfUseAccepted,
    dispatch,
    goToNextNotificationOnClick,
    isNotificationsVisible,
  ]);

  useEffect(() => {
    if (isSideBarOpen && isLaptopVersion) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    };
  }, [isSideBarOpen]);

  if (
    ([STATUS_NOT_REQUESTED, STATUS_LOADING].includes(userInfoStatus) &&
      isLoggedIn) ||
    !isLoggedIn
  ) {
    return <Spinner size="large" className={MainStyles.appSpinner} />;
  }

  if (!areTermsOfUseAccepted && isLoggedIn) {
    return isUserAdditional ? (
      <TermsOfUseRoutes />
    ) : (
      <RegisteredUserTermsRoutes />
    );
  }

  return (
    <div className="App">
      {isLoggedIn && (
        <AppLayout
          registeredCompanies={registeredCompanies}
          currentCompany={currentCompany}
          handleCompanyChange={handleCompanyChange}
          userModules={userAuthorisedModules}
          handleSetApiKey={handleSetApiKey}
          handleIsanetRedirect={handleIsanetRedirect}
          userInfoFullName={userInfoFullName}
          userInfoStatus={userInfoStatus}
          toggleSidebar={isSideBarOpenToggle}
          isSidebarOpen={isSideBarOpen}
          modalProps={modalProps}
          isMainUser={isMainUser}
        >
          <SalaryHeader />
          <Drawer
            placement="left"
            closable={false}
            onClose={isSideBarOpenToggle}
            open={isLaptopVersion ? isSideBarOpen : true}
            getContainer={false}
            className={clsx(Styles.drawer, {
              [Styles.fullDrawer]: isSideBarFullWidth || isLaptopVersion,
            })}
            onMouseEnter={isSideBarFullWidthToggle}
            onMouseLeave={isSideBarFullWidthToggle}
            width={80}
            style={{ position: 'fixed', width: '80px' }}
            zIndex={5}
            mask={!!isLaptopVersion}
            styles={{
              body: {
                height: '100%',
              },
              content: {
                backgroundColor: '#23446C',
              },
              mask: {
                background: 'rgba(255,255,255,0.8)',
              },
            }}
          >
            <SidebarNavLinks
              links={currentCompany.activated ? NAV_LINKS : [HELP_NAV_LINK]}
              handleCloseAndRedirect={handleCloseAndRedirectToIsanet}
              authorisedModules={userAuthorisedModules}
              closeSidebar={isSideBarOpenToggle}
              currentRoute={currentRoute}
              currentCompany={currentCompany}
              handleCustomActions={handleCustomActions}
              isSidebarFullWidth={isLaptopVersion || isSideBarFullWidth}
              isMainUser={isMainUser}
              accountingAccessLinks={accountingAccessLinks}
            />
          </Drawer>
          {!userAuthorisedModules ? (
            <div className={Styles.maintenanceWrapper}>
              <div className={Styles.maintenance}>
                Une partie du site est en maintenance, certains modules sont
                temporairement désactivés. Merci pour votre compréhension
              </div>
              <img
                src={flowersImg}
                alt="flowers"
                className={Styles.flowersImg}
              />
            </div>
          ) : (
            <AuthorizedRoutes
              authorizedModules={userAuthorisedModules}
              userInfoStatus={userInfoStatus}
              isPayEnable={currentCompany?.payEnabled || false}
              isCurrentCompanyActivated={!!currentCompany.activated}
              isMainUser={isMainUser}
              managedSilae={currentCompany?.managedSilae || false}
              silaeProfile={currentCompany?.silaeProfile}
            />
          )}
        </AppLayout>
      )}
    </div>
  );
};
